/**
 * 错误处理工具
 * 用于在测试环境显示详细的错误信息
 */

/**
 * 判断是否为开发/测试环境
 */
export function isDevMode(): boolean {
  return (
    process.env.NODE_ENV === 'development' ||
    process.env.REACT_APP_ENV === 'development' ||
    process.env.REACT_APP_ENV === 'test' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('test') ||
    window.location.hostname.includes('dev')
  );
}

/**
 * 格式化错误信息
 */
export function formatError(error: any): string {
  if (!error) {
    return '未知错误';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    let message = error.message || '未知错误';
    
    // 在开发/测试环境，添加更多详细信息
    if (isDevMode()) {
      const details: string[] = [];
      
      if (error.stack) {
        details.push(`堆栈: ${error.stack}`);
      }
      
      if ((error as any).code) {
        details.push(`错误代码: ${(error as any).code}`);
      }
      
      if ((error as any).response) {
        details.push(`响应: ${JSON.stringify((error as any).response, null, 2)}`);
      }
      
      if ((error as any).config) {
        details.push(`请求配置: ${JSON.stringify((error as any).config, null, 2)}`);
      }
      
      if (details.length > 0) {
        message += '\n\n调试信息:\n' + details.join('\n');
      }
    }
    
    return message;
  }

  // 其他类型的错误
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}

/**
 * 显示错误信息（在测试环境显示详细信息）
 * 使用全局错误管理器显示错误弹窗
 */
let showGlobalErrorFn: ((message: string, title?: string) => void) | null = null;

// 延迟加载，避免循环依赖
function getShowGlobalError() {
  if (!showGlobalErrorFn) {
    try {
      // 动态导入避免循环依赖
      const errorManager = require('./errorManager');
      showGlobalErrorFn = errorManager.showGlobalError;
    } catch (e) {
      console.error('[ErrorHandler] Failed to load errorManager:', e);
    }
  }
  return showGlobalErrorFn;
}

export function showError(error: any, context?: string): void {
  try {
    const errorMessage = formatError(error);
    const fullMessage = context ? `[${context}] ${errorMessage}` : errorMessage;
    
    // 控制台日志
    console.error(`[ErrorHandler] ${context || 'Error'}:`, error);
    
    // 使用全局错误管理器显示错误弹窗
    const showGlobalError = getShowGlobalError();
    
    if (showGlobalError) {
      // 始终显示详细错误信息（包括生产环境），方便调试
      // 如果错误信息太短（少于10个字符），尝试显示更多信息
      let displayMessage = fullMessage;
      if (errorMessage.length < 10 && error) {
        // 尝试从错误对象中提取更多信息
        const details: string[] = [errorMessage];
        if (error instanceof Error && error.stack) {
          details.push(`\n堆栈: ${error.stack.split('\n').slice(0, 3).join('\n')}`);
        }
        if ((error as any)?.response) {
          details.push(`\n响应: ${JSON.stringify((error as any).response, null, 2)}`);
        }
        if ((error as any)?.config) {
          details.push(`\n请求: ${(error as any).config?.method} ${(error as any).config?.url}`);
        }
        displayMessage = details.join('\n');
      }
      
      // 限制错误信息长度，避免过长导致渲染问题
      const maxLength = 5000;
      if (displayMessage.length > maxLength) {
        displayMessage = displayMessage.substring(0, maxLength) + '\n...(内容过长，已截断)';
      }
      
      showGlobalError(displayMessage, '错误');
    } else {
      // 如果无法加载错误管理器，fallback 到 alert
      console.error('[ErrorHandler] ErrorManager not available, using alert');
      alert(fullMessage.substring(0, 1000)); // alert 也限制长度
    }
  } catch (err) {
    // 如果错误处理本身出错，至少输出到控制台
    console.error('[ErrorHandler] Error in showError:', err);
    console.error('[ErrorHandler] Original error:', error);
    // 尝试最简单的 alert
    try {
      alert('发生错误，请查看控制台');
    } catch {
      // 如果连 alert 都失败，至少输出到控制台
      console.error('[ErrorHandler] Cannot show error, all methods failed');
    }
  }
}

/**
 * 显示提示信息（非错误）
 * 用于显示用户操作相关的提示，如"用户取消支付"
 */
export function showInfo(message: string, context?: string): void {
  try {
    const fullMessage = context ? `[${context}] ${message}` : message;
    
    // 控制台日志（使用 logInfo 而不是 logError）
    logInfo(`[Info] ${context || 'Info'}: ${message}`);
    
    // 使用全局错误管理器显示提示弹窗（使用 '提示' 作为标题）
    const errorManager = require('./errorManager');
    if (errorManager && errorManager.showGlobalInfo) {
      errorManager.showGlobalInfo(fullMessage, '提示');
    } else if (errorManager && errorManager.showGlobalError) {
      // 如果没有 showGlobalInfo，使用 showGlobalError 但传入 '提示' 作为标题
      errorManager.showGlobalError(fullMessage, '提示');
    } else {
      // 如果无法加载错误管理器，fallback 到 alert
      alert(`提示: ${fullMessage.substring(0, 1000)}`);
    }
  } catch (err) {
    // 如果提示处理本身出错，至少输出到控制台
    console.log('[ErrorHandler] Error in showInfo:', err);
    console.log('[ErrorHandler] Original message:', message);
    try {
      alert(`提示: ${message.substring(0, 500)}`);
    } catch {
      // 如果连 alert 都失败，至少输出到控制台
      console.log('[ErrorHandler] Cannot show info, all methods failed');
    }
  }
}

/**
 * 记录详细日志（仅在开发/测试环境）
 */
export function logDebug(message: string, data?: any): void {
  if (isDevMode()) {
    console.log(`[Debug] ${message}`, data || '');
  }
}

// 日志上报队列，避免频繁请求
let logQueue: Array<{
  level: string;
  message: string;
  data?: any;
  timestamp: string;
}> = [];
let logQueueTimer: NodeJS.Timeout | null = null;

/**
 * 上报日志到服务器
 */
async function reportLogToServer(level: string, message: string, data?: any): Promise<void> {
  try {
    // 收集上下文信息
    const logData: any = {
      level,
      message,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // 添加 openId（如果有）
    try {
      const openId = localStorage.getItem('openId');
      if (openId) {
        logData.openId = openId;
      }
    } catch (e) {
      // 忽略 localStorage 错误
    }

    // 添加额外数据
    if (data) {
      logData.data = data;
    }

    // 添加到队列
    logQueue.push(logData);

    // 如果队列为空，启动定时器（批量上报）
    if (!logQueueTimer) {
      logQueueTimer = setTimeout(async () => {
        if (logQueue.length === 0) {
          logQueueTimer = null;
          return;
        }

        // 批量上报日志
        const logsToSend = [...logQueue];
        logQueue = [];
        logQueueTimer = null;

        try {
          const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin + '/api';
          await fetch(`${API_BASE_URL}/public/logs`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(logsToSend.length === 1 ? logsToSend[0] : { batch: logsToSend }),
          });
        } catch (error) {
          // 上报失败，静默处理，避免影响用户体验
          console.error('[LogReport] Failed to report logs:', error);
        }
      }, 1000); // 1秒后批量上报
    }

    // 如果是错误日志，立即上报（不等待批量）
    if (level === 'error') {
      // 立即上报当前日志
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin + '/api';
        await fetch(`${API_BASE_URL}/public/logs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(logData),
        });
      } catch (error) {
        // 上报失败，静默处理
        console.error('[LogReport] Failed to report error log:', error);
      }
    }
  } catch (error) {
    // 上报失败，静默处理
    console.error('[LogReport] Failed to prepare log report:', error);
  }
}

/**
 * 记录信息日志
 */
export function logInfo(message: string, data?: any): void {
  console.log(`[Info] ${message}`, data || '');
  
  // 如果是微信授权相关的日志，自动上报
  if (message.includes('WechatAuth') || message.includes('AuthLogin') || message.includes('微信')) {
    reportLogToServer('info', message, data);
  }
}

/**
 * 记录警告日志
 */
export function logWarn(message: string, data?: any): void {
  console.warn(`[Warn] ${message}`, data || '');
  
  // 如果是微信授权相关的日志，自动上报
  if (message.includes('WechatAuth') || message.includes('AuthLogin') || message.includes('微信')) {
    reportLogToServer('warn', message, data);
  }
}

/**
 * 记录错误日志
 */
export function logError(message: string, error?: any): void {
  console.error(`[Error] ${message}`, error || '');
  
  // 错误日志总是上报
  reportLogToServer('error', message, error || undefined);
  
  // 在开发/测试环境，也显示在页面上
  if (isDevMode() && error) {
    const errorMessage = formatError(error);
    console.error(`[Error] 详细错误信息:\n${errorMessage}`);
  }
}


